/**
 * useCompactViewport — ¿la ventana es «baja o estrecha»?
 *
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * En un portátil de 14" (1366×768, y peor con escalado al 125–150 %, que deja
 * el CSS en ~1092×614) la pantalla se queda sin alto útil: barra superior,
 * migas, cabecera del listado, barra de filtros y cabecera de la tabla se
 * comen el viewport. A partir de ahora las pantallas que muestran datos
 * (ListPage y compañía) consultan esta preferencia para apretarse: menos aire,
 * filtros plegados, etc.
 *
 * Es una consulta de MEDIA, así que responde sola al cambiar el tamaño de la
 * ventana (o al conectar un monitor) sin recargar.
 */

import { useEffect, useState } from 'react';

/** Umbrales: alto de portátil de 14" y ancho donde los filtros ya no caben. */
export const COMPACT_QUERY = '(max-height: 820px), (max-width: 1100px)';

/** `MediaQueryList` puede no existir (Jest/jsdom): se responde «no compacto». */
function matches(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

export default function useCompactViewport(query = COMPACT_QUERY) {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [compact, setCompact] = useState(() => matches(query));

  useEffect(() => {
    if (!supported) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (event) => setCompact(event.matches);
    setCompact(mql.matches);
    // Safari < 14 solo tiene addListener; el resto, addEventListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query, supported]);

  return compact;
}
